import { IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useCallback, useState } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import moment from "moment";
import axiosClient from "../../../../../../lib/axios";
import TemplateListagem from "../../../../../../components/BasicComponents/DisplayDados/TemplateListagem";
import { formatarValorMonetario } from "../../../../../../helpers/masks";
import { usePermission } from "../../../../../../components/PermissionGuard";
import { confirm } from "material-ui-confirm";
import { enqueueSnackbar } from "notistack";
import DialogEditarCupom from "./DialogEditarCupom";
import { IDataListaTitulosReceber } from "../../../../../../types/api/financeiro/pesquisar-titulos-receber";
import useDebounce from "../../../../../../lib/useDebounce";
import SearchWithFilters from "../../../../../../components/BasicComponents/SearchWithFilters";

export default function Listagem() {

    const queryClient = useQueryClient();

    const [page, setPage] = useState<number>(0);
    const [palavraChave, setPalavraChave] = useState("");
    const [rowsPerPage, setRowsPerPage] = useState<number>(10);
    const [openEdit, setOpenEdit] = useState<TCuponsDesconto | null>(null);

    const debouncedSearchTerm = useDebounce(palavraChave);

    //queries e mutations
    const { data, isLoading } = useQuery({
        queryKey: ["pesquisar-cupons", page, rowsPerPage, debouncedSearchTerm],
        queryFn: async () => {
            const response = await axiosClient.get(
                "/financeiro/cupons/pesquisar-cupons",
                {
                    params: {
                        pesquisar: debouncedSearchTerm,
                        page: page + 1,
                        limit: rowsPerPage
                    },
                }
            );
            return response.data;
        },
        keepPreviousData: true,
    });

    const { mutate: deletarCupom, isLoading: isLoadingDelete } = useMutation(
        {
            mutationKey: "excluir-cupom",
            mutationFn: (id: string) =>
                axiosClient.delete(`/financeiro/cupons/excluir-cupom/${id}`),
            onError: (error) => {
                console.error(error);
                enqueueSnackbar("Erro ao excluir cupom!", { variant: "error" });
            },
            onSuccess: () => {
                queryClient.invalidateQueries("pesquisar-cupons");
                enqueueSnackbar("Exclusão de cupom realizada com sucesso!", {
                    variant: "success",
                });
            },
        }
    );
    //

    const podeEditar = usePermission('financeiro.cupons-editar-cupom');
    const podeExcluir = usePermission('financeiro.cupons-excluir-cupom');

    const handleDeleteCupom = useCallback(
        async (id: string) => {
            confirm({
                title: "Confirmação",
                description: "Deseja realmente deletá-lo?",
                confirmationText: "Excluir",
                confirmationButtonProps: { color: "error", variant: "contained" },
                cancellationText: "Cancelar",
                dialogProps: { maxWidth: "xs", fullWidth: true },
            })
                .then(() => deletarCupom(id))
                .catch(() => { });
        },
        [deletarCupom, confirm]
    );

    return (
        <>
            <Paper
                elevation={2}
                sx={{
                    p: 2,
                }}>
                <SearchWithFilters
                    palavraChave={palavraChave}
                    textFieldProps={{
                        label: "Pesquisar",
                        placeholder: "Pesquise por criador, código ou nome do público",
                        size: "medium",
                    }}
                    pesquisar={(term) => { setPalavraChave(term); setPage(0); }}
                    setTermoPesquisa={setPalavraChave}
                    hasFilters={false}
                />
                <TemplateListagem
                    isLoading={isLoading}
                    isEmpty={data?.data.length === 0}
                >
                    <TableContainer>
                        <Table
                            size="small"
                            sx={{ minWidth: 650 }}
                            aria-label="simple table"
                        >
                            <TableHead>
                                <TableRow>
                                    <TableCell>
                                        <b>Código</b>
                                    </TableCell>
                                    <TableCell>
                                        <b>Público</b>
                                    </TableCell>
                                    <TableCell align="right">
                                        <b>Valor</b>
                                    </TableCell>
                                    <TableCell>
                                        <b>Criado por</b>
                                    </TableCell>
                                    <TableCell>
                                        <b>Data validade</b>
                                    </TableCell>
                                    <TableCell align="center"></TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {data?.data.map((row: TCuponsDesconto) => (
                                    <TableRow
                                        key={row.id}
                                        sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                                    >
                                        <TableCell component="th" scope="row">
                                            {row.codigo}
                                        </TableCell>
                                        <TableCell component="th" scope="row">
                                            {row.titulos_receber && row.titulos_receber.length > 0 ? row.titulos_receber[0].nome_publico : '-'}
                                        </TableCell>
                                        <TableCell align="right">{formatarValorMonetario(row.valor)}</TableCell>
                                        <TableCell>{row.criado_por}</TableCell>
                                        <TableCell>{moment(row.data_validade).format("DD/MM/YYYY")}</TableCell>
                                        <TableCell align="center">
                                            <IconButton
                                                aria-label="delete"
                                                onClick={() => setOpenEdit(row)}
                                                disabled={!podeEditar || isLoadingDelete}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton
                                                aria-label="delete"
                                                onClick={() => handleDeleteCupom(row.id)}
                                                disabled={!podeExcluir || isLoadingDelete}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </TemplateListagem>

                <TablePagination
                    rowsPerPageOptions={[10, 20, 25]}
                    component="div"
                    disabled={isLoading}
                    count={data?.total ?? 0}
                    page={page}
                    onPageChange={(_, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(event) => {
                        setRowsPerPage(parseInt(event.target.value, 10));
                        setPage(0);
                    }}
                    labelRowsPerPage="Linhas por página"
                    labelDisplayedRows={() => `Pag. ${page + 1}`}
                />
            </Paper>
            {
                openEdit &&
                <>
                    <DialogEditarCupom
                        open={Boolean(openEdit)}
                        defaultValue={openEdit}
                        handleClose={() => setOpenEdit(null)}
                    />
                </>
            }
        </>
    )
}

export type TCuponsDesconto = {
    id: string;
    codigo: string;
    valor: number;
    criado_por: string;
    criado_em: string;
    data_validade: string | null;
    titulos_receber: IDataListaTitulosReceber[] | null;
}